import { useEffect, useRef } from 'react';
import { trackEvent } from 'lib/analytics';
import styled from 'styled-components';

import { mqSmallMax } from 'styled/tools/media-queries';

interface ReviewsDisplayProps {
  product: any;
  url: string;
  imageUrl: string;
  price?: string; // Optional override for product.price (e.g., sale price for schema)
}

const StyledReviewSection = styled.div`
  max-width: 100vw;
  background-color: #f9f9f9;
  padding-top: 48px;
  padding-bottom: 24px;
  overflow: hidden;

  @media ${mqSmallMax} {
    padding-top: 24px;
  }
`;

export const ReviewsDisplay = ({
  product,
  url,
  imageUrl,
  price,
}: ReviewsDisplayProps) => {
  const widgetRef = useRef<HTMLDivElement | null>(null);

  const handleClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    let buttonName = '';

    if (target.id === 'searchInput') {
      buttonName = 'filter';
    } else if (target.classList.contains('yotpo-dropdown-option')) {
      buttonName = 'sort';
    } else if (target.classList.contains('yotpo-reviews-pagination-item')) {
      buttonName = 'Page Click';
    }

    if (buttonName !== '') {
      trackEvent('Button Clicked', {
        buttonName: buttonName,
      });
    }
  };

  // attaching click handlers to yotpo identifiers after render
  // these elements don't exist until the yotpo script runs on the page
  useEffect(() => {
    const widgetElement = widgetRef.current;

    if (widgetElement) {
      widgetElement.addEventListener('click', handleClick, true);
    }

    return () => {
      if (widgetElement) {
        widgetElement.removeEventListener('click', handleClick);
      }
    };
  }, [widgetRef]);
  // we only want to track the first time the user views the widget
  useEffect(() => {
    if (widgetRef.current) {
      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        if (entries[0].isIntersecting) {
          trackEvent('Reviews Widget Viewed');
          observer.unobserve(entries[0].target);
        }
      };

      const observer = new IntersectionObserver(handleIntersection, {
        threshold: 0.1,
      });

      observer.observe(widgetRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [widgetRef]);
  return (
    <StyledReviewSection id='reviews-section'>
      <div
        ref={widgetRef}
        className='yotpo-widget-instance yotpo yotpo-main-widget'
        data-yotpo-instance-id={process.env.NEXT_PUBLIC_YOTPO_REVIEWS_WIDGET_INSTANCE_ID?.toString()}
        data-yotpo-product-id={product.productSku}
        data-yotpo-name={product.name}
        data-yotpo-url={url}
        data-yotpo-image-url={imageUrl}
        data-yotpo-price={price || product.price}
        data-yotpo-currency={product.currency}
        data-yotpo-description={product.description}
      ></div>
    </StyledReviewSection>
  );
};
